import { browserLogger } from '@newfront-insurance/dd-rum';
import { AuthOptions } from '@newfront-insurance/ms-graph-auth';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import type { NovaSettingsData, NovaSettingField } from '../../../hooks/use-nova-settings';
import { useMSGraphAuth } from '../contexts/ms-graph-context';

export interface OutlookConnectController {
  // State
  outlookConnectEnabled: boolean;

  // Actions
  setOutlookConnectEnabled(enabled: boolean): void;
}

export function useOutlookConnectController({
  msGraphEnabled,
  isChatVisible,
  notify,
  serverSettings,
  onPersist,
}: {
  msGraphEnabled: boolean;
  isChatVisible: boolean;
  notify: { success: (msg: string) => void; error: (msg: string) => void };
  serverSettings?: NovaSettingsData | null;
  onPersist?: (field: NovaSettingField, value: unknown) => void;
}): OutlookConnectController {
  const msGraphAuth = useMSGraphAuth();

  // Initialize from server settings if available, default to false
  const [outlookConnectEnabled, setOutlookConnectEnabledState] = useState(
    () => serverSettings?.outlookConnectEnabled ?? false,
  );

  // Sync from server settings when they arrive
  useEffect(() => {
    if (serverSettings != null) {
      setOutlookConnectEnabledState(serverSettings.outlookConnectEnabled);
    }
  }, [serverSettings]);

  // Feature flag kill switch - automatically disconnect when flag is OFF
  useEffect(() => {
    if (!msGraphEnabled && msGraphAuth && msGraphAuth.authState.state === AuthOptions.LOGGED_IN) {
      setOutlookConnectEnabledState(false);
      onPersist?.('outlookConnectEnabled', false);
    }
  }, [msGraphEnabled, msGraphAuth, onPersist]);

  // Proactive re-authentication: When Nova window is opened and toggle is ON but user is logged out, trigger login
  // This ensures toggle state always matches auth state when user actively opens Nova (toggle ON = authenticated)
  const authStateValue = msGraphAuth?.authState.state;

  // Skip auto-reconnect on initial mount to prevent a blank popup appearing on page load.
  // In standalone mode isChatVisible starts as true, which would immediately trigger
  // acquireTokenPopup before MSAL has hydrated its cache from browser storage.
  const isInitialMountRef = useRef(true);

  useEffect(() => {
    if (isInitialMountRef.current) {
      isInitialMountRef.current = false;
      return;
    }

    if (
      isChatVisible && // Only when Nova chat window is opened (user deliberately opened it)
      outlookConnectEnabled &&
      msGraphAuth &&
      (authStateValue === AuthOptions.LOGGED_OUT || authStateValue === AuthOptions.INTERACTION_REQUIRED)
    ) {
      browserLogger.info(
        '[NovaProvider] Nova opened with toggle ON but user logged out - triggering re-authentication',
        { authState: authStateValue },
      );
      msGraphAuth
        .login()
        .then((authState) => {
          browserLogger.info('[NovaProvider] Re-authentication completed', {
            newAuthState: authState.state,
            wasSuccessful: authState.state === AuthOptions.LOGGED_IN,
          });
          if (authState.state === AuthOptions.LOGGED_IN) {
            notify.success('Outlook connected successfully');
          }
        })
        .catch((error) => {
          browserLogger.error('[NovaProvider] Re-authentication failed', error);
          // Don't disable toggle on error - user might have just cancelled
        });
    }
  }, [isChatVisible, outlookConnectEnabled, authStateValue, msGraphAuth, notify]);

  // Disable Outlook and reset state
  const disableOutlook = useCallback(() => {
    setOutlookConnectEnabledState(false);
    onPersist?.('outlookConnectEnabled', false);

    // Log out of MS Graph so toggling back ON requires re-authentication
    if (msGraphAuth) {
      msGraphAuth.logout().catch((error) => {
        browserLogger.error('[OutlookConnect] Failed to logout from MS Graph', { error });
      });
    }
  }, [onPersist, msGraphAuth]);

  // Handle authentication cancellation
  const handleAuthCancelled = useCallback(() => {
    disableOutlook();
    notify.error('Outlook authentication was cancelled. Please enable again in Settings.');
  }, [disableOutlook, notify]);

  // Handle authentication failure
  const handleAuthError = useCallback(
    (error: Error) => {
      disableOutlook();
      notify.error(`Outlook authentication failed: ${error.message || 'Please try again.'}`);
    },
    [disableOutlook, notify],
  );

  // Enable Outlook and trigger authentication
  const enableOutlook = useCallback(async () => {
    setOutlookConnectEnabledState(true);
    onPersist?.('outlookConnectEnabled', true);

    if (!msGraphAuth) {
      return;
    }

    const isLoggedIn = msGraphAuth.authState.state === AuthOptions.LOGGED_IN;
    if (isLoggedIn) {
      // Already logged in - no toast needed, user just toggled ON
      return;
    }

    try {
      const authState = await msGraphAuth.login();
      if (authState.state === AuthOptions.LOGGED_IN) {
        // Show toast only after successful authentication
        notify.success('Outlook connected successfully');
      } else {
        handleAuthCancelled();
      }
    } catch (error) {
      handleAuthError(error as Error);
    }
  }, [msGraphAuth, handleAuthCancelled, handleAuthError, notify, onPersist]);

  // Handle Outlook Connect toggle changes
  const setOutlookConnectEnabled = useCallback(
    (enabled: boolean) => {
      if (enabled) {
        enableOutlook();
      } else {
        disableOutlook();
      }
    },
    [enableOutlook, disableOutlook],
  );

  return useMemo(
    () => ({
      outlookConnectEnabled,
      setOutlookConnectEnabled,
    }),
    [outlookConnectEnabled, setOutlookConnectEnabled],
  );
}
